import { type RefObject, useEffect } from 'react';
import { edgeScrollSpeed } from 'v5/patterns/page-layout/marqueeGeometry';
import {
  scrollerOf,
  viewportOf
} from 'v5/patterns/page-layout/SelectionLayout';

/**
 * Safari on a Mac is the browser that doesn't scroll a page by itself while something is dragged
 * near its edge; React Aria does it there (`useAutoScroll`), and the others do it natively.
 */
export const dragNeedsOwnEdgeScroll = (
  userAgent: string,
  maxTouchPoints: number
) =>
  /AppleWebKit/.test(userAgent) &&
  !/Chrome|Chromium|Android/.test(userAgent) &&
  !/iPhone|iPad|iPod/.test(userAgent) &&
  maxTouchPoints < 2;

/**
 * While something is dragged over a list that the page around scrolls, the page scrolls when the
 * pointer is by the edge of its visible area, faster the deeper it goes (Safari only, see
 * `dragNeedsOwnEdgeScroll`). React Aria's own scrolling reads the pointer against the list, which
 * is as tall as all its items, not against the page's scroller, so the list takes it over
 * (`overflow-y-auto` makes the list its own, never scrolled, scroller in React Aria's eyes).
 */
export function useDragEdgeScroll(
  list: RefObject<HTMLElement | null>,
  scrollsWithPage: boolean
) {
  useEffect(() => {
    const element = list.current;
    if (
      !scrollsWithPage ||
      !element ||
      !dragNeedsOwnEdgeScroll(navigator.userAgent, navigator.maxTouchPoints)
    ) {
      return;
    }
    // a drag that began on the list goes on over the page's sticky bars, which are not in it
    let dragging = false;
    let frame: number | null = null;
    let speed = { x: 0, y: 0 };
    const stopScrolling = () => {
      speed = { x: 0, y: 0 };
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };
    const stop = () => {
      dragging = false;
      stopScrolling();
    };
    const step = (scroller: Element) => {
      scroller.scrollBy(speed.x, speed.y);
      frame = requestAnimationFrame(() => step(scroller));
    };
    const onDragStart = (event: DragEvent) => {
      dragging = event.target instanceof Node && element.contains(event.target);
    };
    const onDragOver = (event: DragEvent) => {
      if (
        !dragging &&
        !(event.target instanceof Node && element.contains(event.target))
      ) {
        stopScrolling();
        return;
      }
      const scroller = scrollerOf(element.parentElement ?? element);
      const view = viewportOf(scroller);
      speed = {
        x: edgeScrollSpeed(event.clientX, view.left, view.right),
        y: edgeScrollSpeed(event.clientY, view.top, view.bottom)
      };
      if (speed.x === 0 && speed.y === 0) stopScrolling();
      else if (frame === null) step(scroller);
    };
    window.addEventListener('dragstart', onDragStart, true);
    window.addEventListener('dragover', onDragOver, true);
    window.addEventListener('dragend', stop, true);
    window.addEventListener('drop', stop, true);
    return () => {
      stop();
      window.removeEventListener('dragstart', onDragStart, true);
      window.removeEventListener('dragover', onDragOver, true);
      window.removeEventListener('dragend', stop, true);
      window.removeEventListener('drop', stop, true);
    };
  }, [list, scrollsWithPage]);
}
